import { useState, useEffect, useRef } from 'react';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const handleMobileLinkClick = () => {
    setMobileMenuOpen(false);
  };

  const brandColor = scrolled ? 'text-foreground-900' : 'text-white';

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-md border-b border-background-200/70'
            : 'bg-transparent'
        }`}
      >
        <div className="w-full px-4 md:px-10">
          <div className="flex items-center justify-between h-14 md:h-[72px]">
            {/* Brand */}
            <a
              href="/"
              className={`text-lg md:text-xl font-heading font-bold whitespace-nowrap transition-colors ${brandColor}`}
            >
              CSSSpreadsheet
            </a>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-4">
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    scrolled
                      ? 'border-foreground-300 text-foreground-700 hover:border-foreground-500 hover:bg-background-100'
                      : 'border-white/40 text-white hover:border-white/70 hover:bg-white/10'
                  }`}
                >
                  Categories
                  <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}>
                    <i className="ri-arrow-down-s-line text-sm"></i>
                  </span>
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full mt-2 left-0 w-64 bg-white rounded-xl border border-background-200 shadow-lg overflow-hidden animate-[fadeIn_0.2s_ease-out]">
                    <div className="max-h-80 overflow-y-auto py-2">
                      {categories.map((cat) => (
                        <a
                          key={cat.name}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-50 hover:text-accent-700 transition-colors duration-150"
                          onClick={() => setDropdownOpen(false)}
                        >
                          <span className="w-5 h-5 flex items-center justify-center">
                            <i className={`${cat.icon} text-base`}></i>
                          </span>
                          <span className="font-medium whitespace-nowrap">{cat.name}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  scrolled
                    ? 'bg-primary-500 text-white hover:bg-primary-600'
                    : 'bg-white text-primary-700 hover:bg-white/90'
                }`}
              >
                cssbuy sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className={`px-5 py-2 rounded-full text-sm font-semibold border transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  scrolled
                    ? 'border-primary-500 text-primary-600 hover:bg-primary-50'
                    : 'border-white text-white hover:bg-white/10'
                }`}
              >
                Buy It Now
              </a>
            </div>

            {/* Mobile Hamburger */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                scrolled ? 'text-foreground-900' : 'text-white'
              }`}
              aria-label="Open menu"
            >
              <i className="ri-menu-line text-2xl"></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-[60] transition-opacity duration-300 md:hidden ${
          mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={() => setMobileMenuOpen(false)}
        />

        {/* Panel */}
        <div
          className={`absolute top-0 right-0 w-[min(340px,85vw)] h-full bg-white flex flex-col shadow-2xl transition-transform duration-300 ${
            mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          {/* Panel Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-background-200/70">
            <span className="text-lg font-heading font-bold text-foreground-900">
              CSSSpreadsheet
            </span>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="w-10 h-10 flex items-center justify-center rounded-lg text-foreground-500 hover:text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer"
              aria-label="Close menu"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>

          {/* Panel Body */}
          <div className="flex-1 overflow-y-auto">
            {/* CTA Buttons */}
            <div className="px-5 py-4 flex flex-col gap-3 border-b border-background-200/70">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={handleMobileLinkClick}
                className="w-full px-5 py-3 rounded-full text-sm font-semibold bg-primary-500 text-white hover:bg-primary-600 transition-colors text-center whitespace-nowrap cursor-pointer"
              >
                cssbuy sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={handleMobileLinkClick}
                className="w-full px-5 py-3 rounded-full text-sm font-semibold border border-primary-500 text-primary-600 hover:bg-primary-50 transition-colors text-center whitespace-nowrap cursor-pointer"
              >
                Buy It Now
              </a>
            </div>

            {/* Article Link */}
            <div className="px-5 py-4 border-b border-background-200/70">
              <a
                href="/article/cssbuy-spreadsheet-guide"
                onClick={handleMobileLinkClick}
                className="flex items-center gap-3 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer"
              >
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className="ri-article-line text-base"></i>
                </span>
                <span>CSSBuy Spreadsheet Guide</span>
              </a>
            </div>

            {/* Categories */}
            <div className="px-5 py-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-3">
                Categories
              </h3>
              <div className="flex flex-col gap-1">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={handleMobileLinkClick}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-accent-50 hover:text-accent-700 transition-colors duration-150"
                  >
                    <span className="w-7 h-7 flex items-center justify-center rounded-md bg-background-100">
                      <i className={`${cat.icon} text-sm`}></i>
                    </span>
                    <span className="font-medium">{cat.name}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Panel Footer */}
          <div className="px-5 py-4 border-t border-background-200/70">
            <p className="text-xs text-foreground-400 text-center">
              The Largest CSSBuy Spreadsheet Directory
            </p>
          </div>
        </div>
      </div>
    </>
  );
}